import { normalizeClosedVisualViewportSnapshot } from "./normalize-closed-visual-viewport";
import type { VisualViewportCssSnapshot } from "./sync-visual-viewport-css";
import {
  KEYBOARD_CLOSE_OFFSET_PX,
  KEYBOARD_OPEN_DATA_ATTR_THRESHOLD_PX,
} from "./visual-viewport-keyboard-thresholds";

export {
  KEYBOARD_CLOSE_OFFSET_PX,
  KEYBOARD_OPEN_DATA_ATTR_THRESHOLD_PX,
  KEYBOARD_OPEN_OFFSET_PX,
} from "./visual-viewport-keyboard-thresholds";

/** Wait for the iOS keyboard animation before locking layout metrics. */
export const KEYBOARD_FREEZE_SETTLE_MS = 280;

let frozenSnapshot: VisualViewportCssSnapshot | null = null;
let freezeTimer: ReturnType<typeof setTimeout> | null = null;
let onFrozenApplied: (() => void) | null = null;

export function registerVisualViewportFreezeListener(listener: () => void): void {
  onFrozenApplied = listener;
}

export function getFrozenVisualViewportSnapshot(): VisualViewportCssSnapshot | null {
  return frozenSnapshot;
}

export function clearVisualViewportFreeze(): void {
  frozenSnapshot = null;
  if (freezeTimer) {
    clearTimeout(freezeTimer);
    freezeTimer = null;
  }
}

export function resolveVisualViewportSnapshot(
  raw: VisualViewportCssSnapshot,
  readLatest: () => VisualViewportCssSnapshot | null,
): VisualViewportCssSnapshot {
  if (raw.keyboardOffsetPx < KEYBOARD_CLOSE_OFFSET_PX) {
    clearVisualViewportFreeze();
    return normalizeClosedVisualViewportSnapshot(raw);
  }

  if (frozenSnapshot) {
    return frozenSnapshot;
  }

  scheduleVisualViewportFreeze(readLatest);
  return raw;
}

function scheduleVisualViewportFreeze(readLatest: () => VisualViewportCssSnapshot | null) {
  if (freezeTimer) {
    clearTimeout(freezeTimer);
  }

  freezeTimer = setTimeout(() => {
    freezeTimer = null;
    const latest = readLatest();
    if (!latest || latest.keyboardOffsetPx < KEYBOARD_OPEN_DATA_ATTR_THRESHOLD_PX) {
      return;
    }
    frozenSnapshot = latest;
    onFrozenApplied?.();
  }, KEYBOARD_FREEZE_SETTLE_MS);
}
